import * as React from 'react';
import {useEffect, useState} from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import {EntityType, Entry, PropertyDefinition} from '../types';
import {Box, DialogTitle} from '@mui/material';
import FormTemplate from './FormTemplate';
import {
	entriesUpdateMany,
	repositoryEntryUpdateLocalAction,
	selectAllEntries,
	selectAllRepositoryEntries,
	selectEntryReadableValues,
} from '../containers/contentSlice';
import {useRepositoryEntryUpdateMutation} from '../services/repositoryEntries';
import {useDispatch} from 'react-redux';
import {useAppSelector} from '../hooks';
import {initFormData, populateSelectOptionsRepositories, populateSelectOptionsRepositoryEntries, populateEntryData, sanitizeEditFormData} from '../common/util';
import {useEntryUpdateMutation} from '../services/records';
import {usePropertyDefinitionUpdateMutation} from '../services/propertyDefinitions';
import { useUpdateNoteMutation } from '../services/notes';
import {PROPERTY_DEFINITION_TYPES} from '../constants';
import {selectIsAuthenticated} from '../containers/authSlice';
import {
	editFormEntrySetState,
	formInit,
	selectEditFormEntry,
	selectEditFormEntryPropertyDefinitions,
	selectFormData,
	selectFormSelectOptions,
	selectFormTemplateProperties,
	selectIsEditDialogOpen,
	setIsEditDialogOpen,
} from '../containers/formDataSlice';

export type ControlledFormData = Record<string, any>; // TODO: use ModelTemplate?

interface DialogProps {
	open: boolean;
	handleClose: () => void;
	handleSubmit: (formData: ControlledFormData) => void;
	title: string;
}

export const useEditForm = () => {
	const dispatch = useDispatch();
	const isEditDialogOpen = useAppSelector(selectIsEditDialogOpen);
	const editFormEntry = useAppSelector(selectEditFormEntry);
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const [entryUpdateMutation] = useEntryUpdateMutation();
	const [updateNoteMutation] = useUpdateNoteMutation();
	const [propertyDefinitionUpdateMutation] = usePropertyDefinitionUpdateMutation();
	const [repositoryEntryUpdateMutation] = useRepositoryEntryUpdateMutation();

	const openEditDialog = (entry: Entry) => {
		dispatch(editFormEntrySetState(entry));
		dispatch(setIsEditDialogOpen(true));
	};

	const closeEditDialog = () => {
		dispatch(setIsEditDialogOpen(false));
	};

	const onEditFormSubmit = (payload: ControlledFormData) => {
		const entry = editFormEntry as Entry;
		const id = entry.id;
		if (isAuthenticated) {
			if (PROPERTY_DEFINITION_TYPES.includes(entry.type as any)) {
				propertyDefinitionUpdateMutation({
					id,
					record: payload,
					repositoryId: (entry as PropertyDefinition).parentId,
				});
			} else {
				if (entry.type === EntityType.Note) {
					updateNoteMutation({
						id,
						patch: payload,
					});
				} else {
					entryUpdateMutation([{ ...payload, id }] as Entry[]);
				}
			}
		} else {
			dispatch(entriesUpdateMany([{ ...payload, id }]));
		}
		closeEditDialog();
	};

	const onEditFormSubmitEntry = (payload: ControlledFormData) => {
		if (!editFormEntry) {
			return;
		}
		const {...propertyValues} = payload; // TODO: type doesn't need to come from the payload
		if (isAuthenticated) {
			repositoryEntryUpdateMutation({ id: editFormEntry.id as string, values: propertyValues});
		} else {
			dispatch(repositoryEntryUpdateLocalAction({ id: editFormEntry.id as string, values: propertyValues }));
		}
		closeEditDialog();
	};

	return {isEditDialogOpen, editFormEntry, openEditDialog, closeEditDialog, onEditFormSubmit, onEditFormSubmitEntry};
};

export const EditFormDialog = ({open, handleClose, handleSubmit, title}: DialogProps) => {
	const [dialogTitle, setDialogTitle] = useState<string | null>(null);
	const entry = useAppSelector(selectEditFormEntry);
	const formData = useAppSelector(selectFormData);
	const formTemplateProperties = useAppSelector(selectFormTemplateProperties);
	const formSelectOptions = useAppSelector(selectFormSelectOptions);
	const dispatch = useDispatch();
	const allEntries = useAppSelector(selectAllEntries);
	const allRepositoryEntries = useAppSelector(selectAllRepositoryEntries);
	const entryReadableValues = useAppSelector(selectEntryReadableValues);
	const propertyDefinitions = useAppSelector(selectEditFormEntryPropertyDefinitions);

	// cache content to prevent it from disappearing during dialog fade out
	useEffect(() => {
		if (open) {
			setDialogTitle(title);
		}
	}, [open]);

	const onSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		const formData = new FormData(e.target as HTMLFormElement);
		handleSubmit(sanitizeEditFormData(propertyDefinitions, formData));
	};

	const initForm = (entry: Entry) => {
		const populatedEntry = entry.type === EntityType.RepositoryEntry ? entry : populateEntryData(entry);
		const formSelectOptions = entry.type === EntityType.RepositoryEntry
			? populateSelectOptionsRepositoryEntries(propertyDefinitions, allRepositoryEntries, entryReadableValues)
			: populateSelectOptionsRepositories(propertyDefinitions, allEntries);
		const formData = initFormData(propertyDefinitions, (populatedEntry as Entry & {data: any}).data);
		dispatch(formInit({
			formTemplateProperties: propertyDefinitions,
			formSelectOptions,
			formData,
		}));
	};

	useEffect(() => {
		if (entry && open) {
			initForm(entry);
		}
	}, [entry, open]);

	if (!entry) {
		return null;
	}

	return <Dialog open={open} onClose={handleClose} 			maxWidth='xs'
		fullWidth>
		<Box display='flex' justifyContent='space-between' alignItems='center'>
			<DialogTitle>{dialogTitle}</DialogTitle>
		</Box>
		<form onSubmit={onSubmit} autoComplete='off'>
			<DialogContent>
				<FormTemplate
					propertyDefinitions={formTemplateProperties}
					// onValueChange={onValueChange}
					formData={formData}
					selectOptions={formSelectOptions}
				/>
			</DialogContent>
			<DialogActions color='secondary'>
				<Button onClick={handleClose}>Cancel</Button>
				<Button type='submit' variant="contained">Update</Button>
			</DialogActions>
		</form>
	</Dialog>;
};

